import React, { useState } from 'react'
import styled from 'styled-components'

import useDefer from '../../common/hooks/useDefer'
import loadPartners, { IPartner } from '../loadPartners'
import Text from '../../ui/Text'
import { Company } from '../loadCompanies'
import { theme } from '../theme'
import { Button } from '../../common/components/Button'
import useText from '../../common/hooks/useText'
import { useCompanyTransferBatchTransferMutation } from '../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'

const SWrapper = styled.div`
    @media (max-width: 900px) {
        padding: 10px 20px;
    }
`

const SColumnTitle = styled.th<{ width?: string; centered?: boolean }>`
    font-weight: 400;
    font-size: 12px;
    font-weight: 700;
    border: 0;
    padding: 6px 10px;
    text-align: ${({ centered }) => (centered ? 'center' : 'start')};
    background-color: white;
    border-bottom: 1px solid #dddddd;
`

const SContainer = styled.div<{ center?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: ${({ center }) => (center ? 'center' : 'flex-start')};
`

const SSelectedOrganisations = styled.div`
    font-size: 16px;
    font-weight: 400;
    padding-bottom: 16px;
`

const SSelectPartnerLabel = styled.label`
    display: block;
    font-size: 16px;
    font-weight: 350;
`

const SInput = styled.input`
    flex: 1;
    margin-right: 10px;
    height: 34px;
    padding: 6px 12px;
    font-size: 14px;
    line-height: 1em;
    color: #555;
    font-weight: 300;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
`

const SSelectPartner = styled.div`
    width: 100%;
    padding-bottom: 20px;
`

const SActions = styled.div<{ centered?: boolean }>`
    width: 100%;
    display: flex;
    gap: 16px;
    justify-content: ${({ centered }) => (centered ? 'center' : 'flex-end')};
    justify-content: flex-end;
    padding: 20px 0 0;
`
const SFixedHeadTableWrapper = styled.div`
    width: 100%;
    min-height: 70px;
    max-height: 400px;
    overflow-y: auto;

    & thead th {
        position: sticky;
        top: 0px;
    }
`

const SPartnersTable = styled.table`
    margin-bottom: 20px;
    border-collapse: collapse;
    width: 100%;
`

const SHead = styled.thead``

const STableRow = styled.tr`
    cursor: pointer;
    border-bottom: 1px solid #dddddd;

    :nth-child(odd) {
        background: #f5f5f5;
    }
`

const SConfirmationText = styled.div`
    text-align: center;
    font-weight: 400;
    font-size: 16px;
    max-width: 390px;
    padding-bottom: 8px;
`

const SSuccessText = styled.p`
    margin: 0;
    padding-bottom: 66px;
    font-size: 16px;
    font-weight: 400;
`

const SConfirmationSubtext = styled.p`
    text-align: center;
    margin: 0;
    max-width: 350px;
    font-size: 14px;
    font-weight: 350;
    padding-bottom: 36px;
`

const SIcon = styled.svg`
    margin: 28px 0;
`

const SHeader = styled.h3`
    font-size: 24px;
    font-weight: 500;
    padding-bottom: 16px;
    font-family: ${({ theme }) => theme.font.extendedName};
    margin: 0;
`

const SColumnCell = styled.td<{ centered?: boolean }>`
    font-size: 14px;
    font-weight: 300;
    padding: 7px 10px 15px 10px;
    vertical-align: top;
    text-align: ${({ centered }) => (centered ? 'center' : 'start')};
`

const SSearchPartnerForm = styled.form`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
`

export type MoveOrganisationsModalProps = {
    close: () => void
    organisations: Array<Company>
    currentPartner: IPartner
    onMove: () => void
}

const Wrapper = ({ children, center }: { children: React.ReactNode; center?: boolean }) => (
    <SWrapper>
        <SHeader data-testid="moveOrgTitle">
            <Text code="lblMoveOrganisation" />
        </SHeader>
        <SContainer center={center}>{children}</SContainer>
    </SWrapper>
)

enum Step {
    SELECT_ACCOUNT = 'SelectAccount',
    CONFIRMATION = 'Confirmation',
    SUCCESS = 'Success',
}

export function MoveOrganisationsModal({
    organisations,
    close,
    currentPartner,
    onMove,
}: MoveOrganisationsModalProps): React.ReactElement {
    const text = useText()
    const [step, setStep] = useState<Step>(Step.SELECT_ACCOUNT)
    const [partnerId, setPartnerId] = useState('')
    const [searchedPartners, setSearchedPartners] = useState<Array<IPartner>>([])
    const [selectedPartner, setSelectedPartner] = useState<IPartner | null>(null)
    const [isFirstSearchCompleted, setIsFirstSearchCompleted] = useState(false)
    const [movedOrganisations, setMovedOrganisations] = useState(0)
    const [batchTransfer] = useCompanyTransferBatchTransferMutation()
    const { value: allPartners } = useDefer(loadPartners, [], [])

    const moveOrganisations = (selectedPartnerId: number) => {
        return batchTransfer({
            body: organisations.map((organisation) => organisation.id),
            partnerId: selectedPartnerId,
        }).unwrap()
    }

    const searchPartner = () => {
        setIsFirstSearchCompleted(true)
        setSearchedPartners(
            allPartners
                ?.filter((partner) => partner.id !== currentPartner.id)
                .filter(
                    (partner) =>
                        partner.id === +partnerId ||
                        partner.name.toLocaleLowerCase().includes(partnerId.toLocaleLowerCase())
                ) || []
        )
    }

    switch (step) {
        case Step.SELECT_ACCOUNT: {
            return (
                <Wrapper>
                    <SSelectedOrganisations data-testid="moveOrgInfo">
                        <Text
                            code="lblOrganisationsSelected"
                            values={{ numberOfOrganisations: organisations.length }}
                        />
                    </SSelectedOrganisations>
                    <SSelectPartner>
                        <SSelectPartnerLabel htmlFor="accountName" data-testid="selectOrgTitle">
                            <Text code="lblSelectPartnerAccount" />
                        </SSelectPartnerLabel>
                        <SSearchPartnerForm
                            onSubmit={(event) => {
                                event.preventDefault()
                                searchPartner()
                            }}
                        >
                            <SInput
                                placeholder={text('lblPartnerField')}
                                value={partnerId}
                                onChange={(event) => setPartnerId(event.target.value)}
                                data-testid="moveOrgInput"
                            />
                            <Button size="sm" type="submit" disabled={!partnerId.trim()} data-testid="searchBtn">
                                {text('btnSearch')}
                            </Button>
                        </SSearchPartnerForm>
                    </SSelectPartner>
                    <SFixedHeadTableWrapper>
                        {searchedPartners.length ? (
                            <SPartnersTable>
                                <SHead>
                                    <tr>
                                        <SColumnTitle width="10%" data-testid="theadId">
                                            <Text code="lblId" />
                                        </SColumnTitle>
                                        <SColumnTitle width="40%" data-testid="theadName">
                                            <Text code="lblName" />
                                        </SColumnTitle>
                                        <SColumnTitle width="30%" data-testid="theadDesc">
                                            <Text code="lblDescription" />
                                        </SColumnTitle>
                                        <SColumnTitle width="20%" centered data-testid="theadSelect">
                                            <Text code="lblSelect" />
                                        </SColumnTitle>
                                    </tr>
                                </SHead>
                                <tbody>
                                    {searchedPartners.map((partner) => (
                                        <STableRow key={Math.random()} onClick={() => setSelectedPartner(partner)}>
                                            <SColumnCell data-testid={'partner-' + partner.id}>
                                                {partner.id}
                                            </SColumnCell>
                                            <SColumnCell data-testid={'partnerName-' + partner.id}>
                                                {partner.name}
                                            </SColumnCell>
                                            <SColumnCell data-testid={'partnerDesc-' + partner.id}>
                                                {partner.description}
                                            </SColumnCell>
                                            <SColumnCell centered data-testid={'partnerIcon-' + partner.id}>
                                                {selectedPartner?.id === partner.id && (
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={15}
                                                        height={12}
                                                        fill="none"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            clipRule="evenodd"
                                                            d="M13.243.264a1 1 0 0 0-1.413.06l-6.876 7.48-2.54-2.54a1 1 0 0 0-1.414 0l-.707.706a1 1 0 0 0 0 1.414L4.308 11.4a1 1 0 0 0 1.443-.03l8.288-9.017a1 1 0 0 0-.06-1.412l-.736-.677Z"
                                                            fill={theme.brandColors.CA_Green}
                                                        />
                                                    </svg>
                                                )}
                                            </SColumnCell>
                                        </STableRow>
                                    ))}
                                </tbody>
                            </SPartnersTable>
                        ) : (
                            isFirstSearchCompleted && (
                                <span>
                                    <Text code="msgNoSearchResult" />
                                </span>
                            )
                        )}
                    </SFixedHeadTableWrapper>
                    <SActions>
                        <Button size="sm" primary={false} onClick={close} data-testid="cancelBtn">
                            {text('btnCancel')}
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setStep(Step.CONFIRMATION)}
                            disabled={!selectedPartner}
                            data-testid="moveBtn"
                        >
                            {text('btnMove')}
                        </Button>
                    </SActions>
                </Wrapper>
            )
        }
        case Step.CONFIRMATION: {
            return (
                <Wrapper center>
                    <SIcon xmlns="http://www.w3.org/2000/svg" width={60} height={60}>
                        <g clipPath="url(#a)">
                            <path
                                d="m31.77 19-.71.71 9.73 9.73H18v1h22.79l-9.74 9.74.71.71 10.95-10.95L31.77 19Z"
                                fill="#81E18B"
                            />
                        </g>
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M30 .984C13.975.984.984 13.974.984 30c0 16.025 12.99 29.016 29.016 29.016 16.025 0 29.016-12.99 29.016-29.016C59.016 13.975 46.026.984 30 .984ZM0 30C0 13.431 13.431 0 30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30Z"
                            fill="#81E18B"
                        />
                        <defs>
                            <clipPath id="a">
                                <path fill="#fff" transform="translate(18 19)" d="M0 0h24.71v21.89H0z" />
                            </clipPath>
                        </defs>
                    </SIcon>
                    <SConfirmationText>
                        <Text
                            code="msgMoveOrganisationsConfirmation"
                            values={{
                                numberOfOrganisations: organisations.length,
                                currentPartnerName: currentPartner.name,
                                selectedPartnerName: selectedPartner?.name,
                            }}
                        />
                    </SConfirmationText>
                    <SConfirmationSubtext>
                        <Text code="msgAllChargersAndRFIDsWillBeMovedTogether" />
                    </SConfirmationSubtext>
                    <SActions centered>
                        <Button size="sm" primary={false} onClick={() => setStep(Step.SELECT_ACCOUNT)}>
                            {text('btnBack')}
                        </Button>
                        <Button
                            size="sm"
                            onClick={async () => {
                                if (!selectedPartner) return
                                try {
                                    await moveOrganisations(selectedPartner.id)
                                    setMovedOrganisations(organisations.length)
                                    notifySuccess(text('msgOrganisationTransferSuccess'))
                                    setStep(Step.SUCCESS)
                                    onMove()
                                } catch (error) {
                                    notifyError(text('msgOrganisationTransferFailure'))
                                }
                            }}
                            data-testid="btnYes"
                        >
                            {text('btnYes')}
                        </Button>
                    </SActions>
                </Wrapper>
            )
        }
        case Step.SUCCESS:
        default: {
            return (
                <Wrapper center>
                    <SIcon xmlns="http://www.w3.org/2000/svg" width={60} height={60} fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M46.253 20.02 24.929 41.343l-5.604-5.613.696-.695 4.909 4.917 20.627-20.627.696.695Zm-28.62 14.016-3.718-3.725.696-.695 3.719 3.725-.696.695Z"
                            fill="#81E18B"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M30 .984C13.975.984.984 13.974.984 30c0 16.025 12.99 29.016 29.016 29.016 16.025 0 29.016-12.99 29.016-29.016C59.016 13.975 46.026.984 30 .984ZM0 30C0 13.431 13.431 0 30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30Z"
                            fill="#81E18B"
                        />
                    </SIcon>
                    <SSuccessText>
                        <Text code="msgOrganisationsMoved" values={{ numberOfOrganisations: movedOrganisations }} />
                    </SSuccessText>
                    <SActions centered>
                        <Button size="sm" onClick={close} data-testid="btnClose">
                            {text('btnClose')}
                        </Button>
                    </SActions>
                </Wrapper>
            )
        }
    }
}
